/***
*
*   NEW LOOK NOTICE
*   One-line strip explaining the IdentityCheck to StackGo change.
*   Expands in place to the full explainer.
*
*   PROPS
*   dismissible - show a close button; the choice is remembered in localStorage
*   storageKey - localStorage key for the dismissed state (required when dismissible)
*   toggleText - label for the expand control (default: "What's changed")
*   className - extra classes for spacing in the parent layout
*
**********/

import React, { useState } from 'react';
import { Icon } from 'components/lib';
import identityCheckLogo from '../../assets/IdentityCheck_Logo_RGB_Purple.png';

const website = 'https://stackgo.io';
const workflows = [
  { name: 'Intake', slug: 'intake' },
  { name: 'Verify', slug: 'verify' },
  { name: 'Screen', slug: 'screen' },
  { name: 'Assess', slug: 'assess' },
  { name: 'Monitor', slug: 'monitor' },
];

function isDismissed(key){

  try { return !!key && localStorage.getItem(key) === 'true' }
  catch (e){ return false }

}

function Explainer(){

  return (
    <>
      <p className='mb-3 text-sg-slate'>
        StackGo is the app you sign in to. It runs your IdentityCheck verifications alongside
        new workflows, so you can take a client from first contact to ongoing compliance in one place.
      </p>

      <div className='mb-3 flex flex-wrap gap-2'>
        { workflows.map(w => (
          <a key={ w.slug } href={ `${website}/workflow/${w.slug}` } target='_blank' rel='noopener noreferrer'
            className='inline-flex items-center gap-2 rounded-full border border-sg-line bg-white px-3 py-1 text-sm font-semibold text-sg-ink no-underline hover:border-sg-blue-line hover:text-sg-blue'>
            <span className='h-2.5 w-2.5 rounded-full' style={{ background: `var(--sg-wf-${w.slug})` }} aria-hidden='true' />
            { w.name }
          </a>
        ))}
      </div>

      <p className='text-sm text-sg-slate'>
        <strong className='text-sg-ink'>IdentityCheck</strong> is still the name your clients see when
        they verify their identity. <strong className='text-sg-ink'>StackGo</strong> is the name of the
        app your team uses.
      </p>
    </>
  );
}

export function NewLookNotice(props){

  const [dismissed, setDismissed] = useState(props.dismissible && isDismissed(props.storageKey));
  const [open, setOpen] = useState(false);

  if (dismissed)
    return false;

  function dismiss(){

    try { localStorage.setItem(props.storageKey, 'true') }
    catch (e){ /* storage unavailable, hide for this visit only */ }
    setDismissed(true);

  }

  return (
    <div className={ `mx-auto max-w-3xl rounded-xl border border-sg-blue-line bg-sg-blue-tint text-left ${props.className || ''}` }>

      <div className='flex items-center gap-3 px-5 py-3'>
        <img src={ identityCheckLogo } alt='IdentityCheck' className='h-5 w-auto' />
        <span className='flex-1 text-sm text-sg-slate'>
          <strong className='text-sg-ink'>Welcome to our new look.</strong> IdentityCheck is now part of StackGo.
        </span>
        <button type='button' onClick={ () => setOpen(!open) } aria-expanded={ open }
          className='inline-flex items-center gap-1 text-sm font-semibold text-sg-blue'>
          { open ? 'Hide' : (props.toggleText || "What's changed") }
          <Icon image={ open ? 'chevron-up' : 'chevron-down' } size={ 16 } />
        </button>
        { props.dismissible &&
          <button type='button' aria-label='Dismiss' onClick={ dismiss }
            className='rounded-full p-1 text-sg-slate hover:bg-white hover:text-sg-ink'>
            <Icon image='x' size={ 16 } />
          </button> }
      </div>

      { open &&
        <div className='border-t border-sg-blue-line px-5 py-4'>
          <Explainer />
        </div> }

    </div>
  );
}
